import React, { useState, useEffect, useCallback } from 'react';
import { getHomeStats, getHomeCalendar, getHomeJobs } from '../api';

const fmt = (v) => parseFloat(v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtD = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function CalendarView({ calendar, todayStr }) {
  const [monthOffset, setMonthOffset] = useState(0);

  const now = new Date();
  const viewDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  // Build calendar data map
  const calMap = {};
  calendar.forEach(c => { calMap[c.date] = c; });

  // First day of month (0=Sun, convert to Mon=0)
  const firstDay = new Date(year, month, 1).getDay();
  const startPad = firstDay === 0 ? 6 : firstDay - 1; // Mon-based offset
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Build grid cells
  const cells = [];
  // Empty cells before 1st
  for (let i = 0; i < startPad; i++) cells.push(null);
  // Day cells
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, dateStr, data: calMap[dateStr] || null, isToday: dateStr === todayStr });
  }

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">📅 Transaction Calendar</h3>
        <div className="flex items-center space-x-2">
          <button onClick={() => setMonthOffset(o => o - 1)} className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs hover:bg-slate-600">← Prev</button>
          <span className="text-sm text-white font-medium min-w-[140px] text-center">{MONTH_NAMES[month]} {year}</span>
          <button onClick={() => setMonthOffset(o => o + 1)} disabled={monthOffset >= 1} className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs hover:bg-slate-600 disabled:opacity-30">Next →</button>
          {monthOffset !== 0 && <button onClick={() => setMonthOffset(0)} className="px-2 py-1 bg-blue-600 text-white rounded text-xs">Today</button>}
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
          <div key={d} className="text-center text-[10px] text-slate-500 font-bold py-1">{d}</div>
        ))}
        {cells.map((cell, i) => {
          if (!cell) return <div key={`empty-${i}`} className="min-h-[65px]" />;
          const { day, dateStr, data, isToday } = cell;
          const isFuture = new Date(dateStr) > now;
          return (
            <div key={dateStr}
              className={`rounded p-1.5 text-center min-h-[65px] ${isToday ? 'border-2 border-blue-500 bg-blue-500/10' : 'border border-slate-700 bg-slate-700/30'}`}>
              <div className={`text-[10px] ${isToday ? 'text-blue-400 font-bold' : isFuture ? 'text-slate-600' : 'text-slate-400'}`}>{day}</div>
              {data && (
                <div className="mt-0.5 space-y-0.5">
                  {data.approved > 0 && <div className="text-[9px] text-green-400">✅ {data.approved}</div>}
                  {data.declined > 0 && <div className="text-[9px] text-red-400">❌ {data.declined}</div>}
                  {data.revenue > 0 && <div className="text-[9px] text-emerald-400 font-mono">${Math.round(data.revenue)}</div>}
                  {data.planned > 0 && <div className="text-[9px] text-yellow-400">⏳ {data.planned}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center space-x-4 mt-3 text-[10px] text-slate-500">
        <span>✅ Approved</span><span>❌ Declined</span><span className="text-emerald-400">$ Revenue</span><span>⏳ Scheduled</span>
      </div>
    </div>
  );
}

export default function HomeTab({ socket }) {
  const [stats, setStats] = useState({ today: {}, total: {} });
  const [calendar, setCalendar] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [sR, cR, jR] = await Promise.all([getHomeStats(), getHomeCalendar(), getHomeJobs()]);
      if (sR.success) setStats(sR.data);
      if (cR.success) setCalendar(cR.data || []);
      if (jR.success) setJobs(jR.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 15000); return () => clearInterval(i); }, [fetchData]);
  useEffect(() => {
    if (!socket) return;
    const r = () => fetchData();
    ['rebill_task_success', 'rebill_task_declined', 'rebill_job_completed'].forEach(e => socket.on(e, r));
    return () => { ['rebill_task_success', 'rebill_task_declined', 'rebill_job_completed'].forEach(e => socket.off(e, r)); };
  }, [socket, fetchData]);

  const t = stats.today || {};
  const a = stats.total || {};
  const todayStr = new Date().toISOString().split('T')[0];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Dashboard</h2>

      {/* Today Section */}
      <div>
        <h3 className="text-sm font-bold text-slate-400 uppercase mb-3">📅 Today</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">Today Revenue</div>
            <div className="text-2xl font-bold text-green-400">{fmt(t.revenue)}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">Today Approval</div>
            <div className="text-2xl font-bold text-blue-400">{t.approval || 0}%</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">Today Success</div>
            <div className="text-2xl font-bold text-green-400">{t.success || 0}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">Today Declined</div>
            <div className="text-2xl font-bold text-red-400">{t.declined || 0}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">Today Rebill</div>
            <div className="text-xl font-bold text-blue-400">{fmt(t.rebill_revenue)}</div>
            <div className="text-[10px] text-slate-500">{t.rebill_success || 0} success</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">Today AutoRb</div>
            <div className="text-xl font-bold text-purple-400">{fmt(t.autorb_revenue)}</div>
            <div className="text-[10px] text-slate-500">{t.autorb_success || 0} success</div>
          </div>
        </div>
      </div>

      {/* Total Section */}
      <div>
        <h3 className="text-sm font-bold text-slate-400 uppercase mb-3">📊 Total (All Time)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">Total Revenue</div>
            <div className="text-2xl font-bold text-green-400">{fmt(a.revenue)}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">Total Approval</div>
            <div className="text-2xl font-bold text-blue-400">{a.approval || 0}%</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">Total Success</div>
            <div className="text-2xl font-bold text-green-400">{a.success || 0}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">Total Declined</div>
            <div className="text-2xl font-bold text-red-400">{a.declined || 0}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">Rebill CA</div>
            <div className="text-xl font-bold text-blue-400">{fmt(a.rebill_revenue)}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">AutoRb CA</div>
            <div className="text-xl font-bold text-purple-400">{fmt(a.autorb_revenue)}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">Total Clients</div>
            <div className="text-xl font-bold text-white">{a.clients || 0}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="text-[10px] text-slate-500 uppercase">Pending Jobs</div>
            <div className="text-xl font-bold text-yellow-400">{a.pending_jobs || 0}</div>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <CalendarView calendar={calendar} todayStr={todayStr} />

      {/* Recent Jobs */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white">Recent Jobs</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-700/50">
              <th className="px-4 py-2 text-left text-xs text-slate-400 uppercase">Date</th>
              <th className="px-4 py-2 text-left text-xs text-slate-400 uppercase">Type</th>
              <th className="px-4 py-2 text-left text-xs text-slate-400 uppercase">Name</th>
              <th className="px-4 py-2 text-left text-xs text-slate-400 uppercase">Status</th>
              <th className="px-4 py-2 text-center text-xs text-slate-400 uppercase">Clients</th>
              <th className="px-4 py-2 text-center text-xs text-slate-400 uppercase">Success</th>
              <th className="px-4 py-2 text-center text-xs text-slate-400 uppercase">Declined</th>
              <th className="px-4 py-2 text-right text-xs text-slate-400 uppercase">Revenue</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-700/50">
              {jobs.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">No jobs yet</td></tr>
              ) : jobs.slice(0, 20).map(j => (
                <tr key={j.id} className="hover:bg-slate-700/30">
                  <td className="px-4 py-2 text-slate-400 text-xs">{fmtD(j.createdAt || j.created_at)}</td>
                  <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded text-xs ${j.type === 'manual' ? 'bg-blue-500/10 text-blue-400' : j.type === 'autorb' ? 'bg-purple-500/10 text-purple-400' : 'bg-orange-500/10 text-orange-400'}`}>{j.type}</span></td>
                  <td className="px-4 py-2 text-white text-xs">{j.name || `Job #${j.id}`}</td>
                  <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded text-xs ${j.status === 'running' ? 'bg-blue-500/10 text-blue-400' : j.status === 'completed' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>{j.status}</span></td>
                  <td className="px-4 py-2 text-center text-slate-300 text-xs">{j.total_clients}</td>
                  <td className="px-4 py-2 text-center text-green-400 text-xs">{j.success_count}</td>
                  <td className="px-4 py-2 text-center text-red-400 text-xs">{j.declined_count}</td>
                  <td className="px-4 py-2 text-right text-emerald-400 font-bold text-xs">{fmt(j.total_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
